import {
  BillingInvoiceRequestCreate, BillingPaymentMethod, BillingProduct, BillingSubscription, BillingSubscriptionCreate,
  BillingSubscriptionUpdate,
  BillingTransaction
} from '@pollination-solutions/pollination-sdk'
import { notification } from 'antd'
import { useCallback, useState } from 'react'

import { useAuth } from 'auth-context'

const useBillingAPI = () => {
  const { client } = useAuth()
  const [loading, setLoading] = useState(false)

  const listBillingProducts = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await client.billing.listBillingProducts()
      return data as BillingProduct[]
    } catch (error: any) {
      notification.error({
        message: 'Failed to load billing products',
        description: error?.response?.data?.detail || 'An error occurred'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }, [client.billing])

  const createBillingSubscription = useCallback(async (subscriptionData: BillingSubscriptionCreate) => {
    try {
      setLoading(true)
      const { data } = await client.billing.createBillingSubscriptionTransaction({
        billingSubscriptionCreate: subscriptionData
      })
      return data
    } catch (error: any) {
      notification.error({
        message: 'Failed to create billing subscription',
        description: error?.response?.data?.detail || 'An error occurred'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }, [client.billing])

  const createBillingSubscriptionDirect = useCallback(async (subscriptionData: BillingSubscriptionCreate) => {
    try {
      setLoading(true)
      const { data } = await client.billing.createBillingSubscriptionDirect({
        billingSubscriptionCreate: subscriptionData
      })
      return data
    } catch (error: any) {
      notification.error({
        message: 'Failed to create billing subscription directly',
        description: error?.response?.data?.detail || 'An error occurred'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }, [client.billing])

  const updateBillingSubscription = useCallback(async (subscriptionId: string, updateData: BillingSubscriptionUpdate) => {
    try {
      // setLoading(true)
      const { data } = await client.billing.updateBillingSubscription({
        subscriptionId,
        billingSubscriptionUpdate: updateData
      })
      notification.success({
        message: 'Subscription updated successfully!'
      })
      return data
    } catch (error: any) {
      notification.error({
        message: 'Failed to update billing subscription',
        description: error?.response?.data?.detail || 'An error occurred'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }, [client.billing])

  const cancelBillingSubscription = useCallback(async (subscriptionId: string) => {
    try {
      setLoading(true)
      const { data } = await client.billing.cancelBillingSubscription({ subscriptionId })
      notification.success({
        message: 'Subscription cancelled successfully!'
      })
      return data
    } catch (error: any) {
      notification.error({
        message: 'Failed to cancel billing subscription',
        description: error?.response?.data?.detail || 'An error occurred'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }, [client.billing])

  const reactivateBillingSubscription = useCallback(async (subscriptionId: string) => {
    try {
      setLoading(true)
      const { data } = await client.billing.uncancelBillingSubscription({ subscriptionId })
      notification.success({
        message: 'Subscription reactivated successfully!'
      })
      return data
    } catch (error: any) {
      notification.error({
        message: 'Failed to reactivate billing subscription',
        description: error?.response?.data?.detail || 'An error occurred'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }, [client.billing])

  const previewBillingSubscription = useCallback(async (subscriptionId: string, updateData: BillingSubscriptionUpdate) => {
    try {
      setLoading(true)
      const { data } = await client.billing.previewBillingSubscription({
        subscriptionId,
        billingSubscriptionUpdate: updateData
      })
      return data
    } catch (error: any) {
      notification.error({
        message: 'Failed to preview billing subscription changes',
        description: error?.response?.data?.detail || 'An error occurred'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }, [client.billing])

  const listBillingSubscriptionTransactions = useCallback(async (subscriptionId: string) => {
    try {
      setLoading(true)
      const { data } = await client.billing.listBillingSubscriptionTransactions({ subscriptionId })
      return data as BillingTransaction[]
    } catch (error: any) {
      notification.error({
        message: 'Failed to load billing transactions',
        description: error?.response?.data?.detail || 'An error occurred'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }, [client.billing])

  const listBillingSubscriptions = useCallback(async (account: string) => {
    try {
      setLoading(true)
      const { data } = await client.billing.listBillingSubscriptions({ account })
      return data as BillingSubscription[]
    } catch (error: any) {
      notification.error({
        message: 'Failed to load billing subscriptions',
        description: error?.response?.data?.detail || 'An error occurred'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }, [client.billing])

  const listSubscriptionPaymentMethods = useCallback(async (subscriptionId: string) => {
    try {
      setLoading(true)
      const { data } = await client.billing.listSubscriptionPaymentMethods({ subscriptionId })
      return data as BillingPaymentMethod[]
    } catch (error: any) {
      notification.error({
        message: 'Failed to load payment methods',
        description: error?.response?.data?.detail || 'An error occurred'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }, [client.billing])

  const updateSubscriptionPaymentMethod = useCallback(async (subscriptionId: string) => {
    try {
      setLoading(true)
      const { data } = await client.billing.updateSubscriptionPaymentMethod({ subscriptionId })
      return data as BillingTransaction
    } catch (error: any) {
      notification.error({
        message: 'Failed to update payment method',
        description: error?.response?.data?.detail || 'An error occurred'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }, [client.billing])

  const deleteSubscriptionPaymentMethod = useCallback(async (subscriptionId: string, paymentMethodId: string) => {
    try {
      setLoading(true)
      await client.billing.deleteSubscriptionPaymentMethod({ subscriptionId, paymentMethodId })
      notification.success({
        message: 'Payment method deleted successfully!'
      })
    } catch (error: any) {
      notification.error({
        message: 'Failed to delete payment method',
        description: error?.response?.data?.detail || 'An error occurred'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }, [client.billing])

  const sendInvoiceRequestConfirmation = useCallback(async (request: BillingInvoiceRequestCreate) => {
    try {
      setLoading(true)
      request.additional_emails?.push('mostapha@ladybug.tools')
      const { data } = await client.billing.sendInvoiceRequestConfirmation({
        billingInvoiceRequestCreate: request
      })
      notification.success({
        message: 'Invoice request submitted successfully!',
        description: 'You will receive a confirmation email shortly. A member of our staff will reach out within a few business days.'
      })
      return data
    } catch (error: any) {
      notification.error({
        message: 'Failed to submit invoice request',
        description: error?.response?.data?.detail || 'An error occurred'
      })
      throw error
    } finally {
      setLoading(false)
    }
  }, [client.billing])

  return {
    loading,
    listBillingProducts,
    createBillingSubscription,
    createBillingSubscriptionDirect,
    updateBillingSubscription,
    cancelBillingSubscription,
    reactivateBillingSubscription,
    previewBillingSubscription,
    listBillingSubscriptionTransactions,
    listBillingSubscriptions,
    listSubscriptionPaymentMethods,
    updateSubscriptionPaymentMethod,
    deleteSubscriptionPaymentMethod,
    sendInvoiceRequestConfirmation,
  }
}

export default useBillingAPI